import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { recommendationsApi } from '../api/recommendations.api';

const keys = {
  live: (depId) => ['deployment', depId, 'recommendations', 'live'],
  historical: (depId, params) => ['deployment', depId, 'recommendations', 'historical', params],
  detail: (depId, recId) => ['deployment', depId, 'recommendations', 'detail', recId],
  filters:    (depId)              => ['deployment', depId, 'recommendations', 'filters'],
};

export function useRecommendationsLive(depId) {
  return useQuery({
    queryKey: keys.live(depId),
    queryFn: () => recommendationsApi.live(depId),
    enabled: !!depId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

// Paginated history. `data` is { rows, pagination }. keepPreviousData keeps the
// current page visible while the next page loads (no empty flash on page change).
export function useRecommendationsHistorical(depId, params = {}) {
  return useQuery({
    queryKey: keys.historical(depId, params),
    queryFn: () => recommendationsApi.historical(depId, params),
    enabled: !!depId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useRecommendationDetail(depId, recId) {
  return useQuery({
    queryKey: keys.detail(depId, recId),
    queryFn: () => recommendationsApi.detail(depId, recId),
    enabled: !!depId && !!recId,
    staleTime: 30_000,
  });
}

export function useSubmitAction(depId, recId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, comment }) => recommendationsApi.submitAction(depId, recId, { action, comment }),
    onMutate: async ({ action, comment }) => {
      // Cancel in-flight refetches so they don't clobber the optimistic write.
      await qc.cancelQueries({ queryKey: keys.live(depId) });
      await qc.cancelQueries({ queryKey: keys.detail(depId, recId) });

      // Snapshot the caches we are about to patch so onError can restore them.
      const prevLive = qc.getQueryData(keys.live(depId));
      const prevDetail = qc.getQueryData(keys.detail(depId, recId));

      // Optimistically patch rec.status in the live list so the badge updates instantly
      qc.setQueryData(keys.live(depId), (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((r) => r.id === recId ? { ...r, status: action } : r);
      });
      // Optimistically append a timeline entry so StateSection re-derives status immediately
      qc.setQueryData(keys.detail(depId, recId), (old) => {
        if (!old) return old;
        const entry = { id: `optimistic-${Date.now()}`, type: action, actor: null, comment: comment ?? null, timestamp: new Date().toISOString() };
        return { ...old, timeline: [entry, ...(old.timeline ?? [])] };
      });

      return { prevLive, prevDetail };
    },
    onError: (_err, _vars, context) => {
      // Roll back both caches to their pre-mutate snapshots on BE rejection,
      // so the status badge and timeline don't show an action the BE refused.
      if (context?.prevLive !== undefined) qc.setQueryData(keys.live(depId), context.prevLive);
      if (context?.prevDetail !== undefined) qc.setQueryData(keys.detail(depId, recId), context.prevDetail);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.live(depId) });
      // Prefix match invalidates every paginated historical page (the full key is
      // [...'historical', { from, to, page, pageSize }]).
      qc.invalidateQueries({ queryKey: ['deployment', depId, 'recommendations', 'historical'] });
      qc.invalidateQueries({ queryKey: keys.detail(depId, recId) });
    },
  });
}

export function useAddRemark(depId, recId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (comment) => recommendationsApi.addRemark(depId, recId, comment),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.detail(depId, recId) }),
  });
}

export function useRecommendationsFilterSchema(depId) {
  return useQuery({
    queryKey: keys.filters(depId),
    queryFn:  () => recommendationsApi.filters(depId),
    enabled:  !!depId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

